
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import Loadable from "react-loadable";
import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import rootStore from "./store/store";
import { ToastContainer } from "react-toastify";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "react-toastify/dist/ReactToastify.css";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { ThemeProvider } from "./theme/ThemeProvider";
import { initThemeFromStorage } from "./theme/tokens";
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

initThemeFromStorage();

// Prevent CRA/webpack overlay from treating benign ResizeObserver notices as fatal.
const isBenignResizeObserverError = (message) =>
  typeof message === "string" && message.includes("ResizeObserver loop");

window.addEventListener(
  "error",
  (event) => {
    if (isBenignResizeObserverError(event?.message)) {
      event.stopImmediatePropagation();
    }
  },
  true
);
 
/* -------------------- STORE -------------------- */
 
const { store } = rootStore;
 
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false },
    mutations: { retry: false },
  },
});
 
/* -------------------- ROUTER -------------------- */
 
const router = createBrowserRouter([
  {
    path: "*",
    element: <App />,
  },
]);
 
/* -------------------- CHUNK ERROR HANDLING -------------------- */
 
// Prevent infinite reload loop
const CHUNK_RELOAD_KEY = "chunk-reload-attempted";
const CHUNK_ERROR_PATTERNS = [
  "Loading chunk",
  "ChunkLoadError",
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
];

const isChunkLoadError = (value) => {
  const message = value?.message || value?.toString?.() || "";
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

const reloadOnce = () => {
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY) !== "1") {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    window.location.reload();
  }
};
 
// Sync chunk errors
window.addEventListener("error", (event) => {
  if (isChunkLoadError(event?.error) || isChunkLoadError(event?.message)) {
    console.warn("Chunk load failed. Reloading...");
    reloadOnce();
  }
});
 
// Async import() chunk errors (MOST IMPORTANT)
window.addEventListener("unhandledrejection", (event) => {
  if (isChunkLoadError(event?.reason)) {
    console.warn("Chunk load failed (promise). Reloading...");
    reloadOnce();
  }
});
 
/* -------------------- ERROR BOUNDARY (OPTIONAL BUT SAFE) -------------------- */
 
class SilentChunkBoundary extends React.Component {
  static getDerivedStateFromError(error) {
    if (isChunkLoadError(error)) {
      reloadOnce();
    }
    return null;
  }
  render() {
    return this.props.children;
  }
}
 
/* -------------------- RENDER -------------------- */
 
const root = ReactDOM.createRoot(document.getElementById("root"));
 
Loadable.preloadReady().then(() => {
  root.render(
      // <React.StrictMode>
          <Provider store={store}>
              <QueryClientProvider client={queryClient}>
                  <ThemeProvider>
                      <ToastContainer />
                      <SilentChunkBoundary>
                          <RouterProvider router={router} />
                      </SilentChunkBoundary>
                  </ThemeProvider>
              </QueryClientProvider>
          </Provider>
      // </React.StrictMode>
  );
});
 
/* -------------------- SERVICE WORKER -------------------- */

serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    window.dispatchEvent(
      new CustomEvent('appUpdateAvailable', {
        detail: registration,
      })
    );
  },
});
 
reportWebVitals();
